import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ExternalLink, RefreshCw, CheckCircle2, AlertCircle, Loader2, Edit2, Save, X, Link2, ChevronDown, Zap } from 'lucide-react';
import { domainApi, gscApi } from '@/lib/api';
import { formatDate, formatNumber, formatBytes, cn } from '@/lib/utils';
import { FileManager } from '@/components/FileManager/FileManager';
import { DangerZone } from '@/components/DangerZone';
import { IndexNowSettings } from '@/components/IndexNow/IndexNowSettings';
import { LlmsTxtSettings } from '@/components/LlmsTxt/LlmsTxtSettings';
import { ApexProxySettings } from '@/components/ApexProxy/ApexProxySettings';
import { AgentSurfacesSettings } from '@/components/AgentSurfaces/AgentSurfacesSettings';
import { BotAccessSettings } from '@/components/BotAccess/BotAccessSettings';
import { useAuth } from '@/contexts/AuthContext';
import { useState, useEffect, useRef } from 'react';

export function DomainDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { refresh: refreshAuth, pool, limits } = useAuth();
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showSitemapDropdown, setShowSitemapDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Budget editing state
  const [isEditingBudget, setIsEditingBudget] = useState(false);
  const [budgetValue, setBudgetValue] = useState(0);
  const [isSavingBudget, setIsSavingBudget] = useState(false);
  const [budgetError, setBudgetError] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['domain', id],
    queryFn: () => domainApi.get(id!),
    enabled: !!id,
  });

  const { data: gscStatus } = useQuery({
    queryKey: ['gsc-status'],
    queryFn: gscApi.status,
  });

  const domain = data?.domain;
  const sitemaps = data?.sitemaps || [];
  const isGscConnected = gscStatus?.connected;

  // Find the primary sitemap to view (index if available, otherwise first sitemap)
  const primarySitemap = sitemaps.find(s => !!s.isIndex) || sitemaps[0];

  // Calculate available budget for this domain
  const minBudget = limits?.min_urls_per_domain || 100;
  const currentDomainBudget = domain?.urlBudget || 0;
  // Available = pool available + current domain's budget (since we can reallocate)
  const maxAvailableForThisDomain = pool
    ? pool.available === -1
      ? 10000000
      : pool.available + currentDomainBudget
    : currentDomainBudget;

  // Initialize budget value when domain loads
  useEffect(() => {
    if (domain) {
      setBudgetValue(domain.urlBudget);
    }
  }, [domain]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSitemapDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSaveBudget = async () => {
    if (!domain) return;
    setIsSavingBudget(true);
    setBudgetError(null);

    try {
      await domainApi.updateBudget(domain.id, budgetValue);
      await Promise.all([refetch(), refreshAuth()]);
      setIsEditingBudget(false);
    } catch (err) {
      setBudgetError(err instanceof Error ? err.message : 'Failed to update budget');
    } finally {
      setIsSavingBudget(false);
    }
  };

  const handleCancelBudgetEdit = () => {
    setBudgetValue(domain?.urlBudget || 0);
    setBudgetError(null);
    setIsEditingBudget(false);
  };

  const handleVerify = async () => {
    if (!domain) return;
    setIsVerifying(true);
    setVerifyResult(null);

    try {
      const result = await domainApi.verify(domain.id);
      setVerifyResult({
        success: result.verified,
        message: result.message,
      });
      if (result.verified) {
        refetch();
        queryClient.invalidateQueries({ queryKey: ['domains'] });
      }
    } catch (err) {
      setVerifyResult({
        success: false,
        message: err instanceof Error ? err.message : 'Verification failed. Please try again.',
      });
    } finally {
      setIsVerifying(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!domain) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-600">Domain not found</p>
        <Link to="/" className="text-primary hover:underline">
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Dashboard
      </Link>

      {/* Domain Header */}
      <div className="bg-white rounded-xl border shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex flex-col gap-4">
          {/* Domain name and badges */}
          <div>
            <h1 className="text-xl sm:text-2xl font-bold break-all mb-2">{domain.domain}</h1>
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {domain.verified ? (
                <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                  <CheckCircle2 className="w-3 h-3" />
                  Verified
                </span>
              ) : (
                <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                  <AlertCircle className="w-3 h-3" />
                  Pending Verification
                </span>
              )}
              {isGscConnected ? (
                <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                  <Link2 className="w-3 h-3" />
                  GSC Connected
                </span>
              ) : (
                <Link
                  to="/settings"
                  className="flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium hover:bg-slate-200"
                >
                  <Link2 className="w-3 h-3" />
                  Connect GSC
                </Link>
              )}
            </div>
            <p className="text-slate-500 text-sm">
              Added {formatDate(domain.createdAt)}
            </p>
          </div>

          {/* Action button - Split button with dropdown for multiple sitemaps */}
          {domain.verified && primarySitemap && (
            <div className="relative w-full sm:w-auto" ref={dropdownRef}>
              <div className="flex items-center gap-0">
                {/* Main button */}
                <a
                  href={`https://${domain.domain}/${primarySitemap.filename}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 border border-r-0 rounded-l-lg hover:bg-slate-50"
                >
                  <ExternalLink className="w-4 h-4" />
                  View Sitemap
                </a>
                {/* Dropdown toggle */}
                {sitemaps.length > 1 && (
                  <button
                    onClick={() => setShowSitemapDropdown(!showSitemapDropdown)}
                    className="px-3 py-2 border rounded-r-lg hover:bg-slate-50 self-stretch flex items-center"
                  >
                    <ChevronDown className={cn(
                      'w-4 h-4 transition-transform',
                      showSitemapDropdown && 'rotate-180'
                    )} />
                  </button>
                )}
              </div>

              {/* Dropdown menu */}
              {showSitemapDropdown && sitemaps.length > 1 && (
                <div className="absolute left-0 mt-2 w-64 bg-white border rounded-lg shadow-lg z-10 py-1">
                  {sitemaps.map((sitemap) => (
                    <a
                      key={sitemap.id}
                      href={`https://${domain.domain}/${sitemap.filename}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block px-4 py-2 hover:bg-slate-50 text-sm"
                      onClick={() => setShowSitemapDropdown(false)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 truncate">{sitemap.filename}</span>
                        <div className="flex items-center gap-1 ml-2">
                          {!!sitemap.isIndex && (
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                              Sitemap Index
                            </span>
                          )}
                          <ExternalLink className="w-3 h-3 text-slate-400" />
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {formatNumber(sitemap.urlCount)} URLs
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Post-Verification Guidance - shown when verified but no sitemaps yet */}
      {domain.verified && sitemaps.length === 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-green-800 mb-2">Domain Verified!</h3>
              <p className="text-green-700 mb-3">Your domain is ready. Here's what to do next:</p>
              <ol className="list-decimal list-inside text-green-700 space-y-1.5">
                <li>Upload your URLs in the file manager below</li>
                <li>Click "Generate Sitemap" to publish</li>
                <li>We'll automatically notify Google of your new sitemap</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* URL Budget Section */}
      <div className="bg-white rounded-xl border shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold">URL Budget</h2>
            <p className="text-sm text-slate-500">Manage URL allocation for this domain</p>
          </div>
          {!isEditingBudget && (
            <button
              onClick={() => setIsEditingBudget(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
            >
              <Edit2 className="w-4 h-4" />
              Edit
            </button>
          )}
        </div>

        {/* Budget Display/Editor */}
        {isEditingBudget ? (
          <div>
            {/* Pool info when editing */}
            <div className="p-4 bg-slate-50 rounded-lg mb-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-500">Available in pool</span>
                <span className="font-medium">
                  {pool?.available === -1 ? 'Unlimited' : formatNumber(maxAvailableForThisDomain)}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Minimum per domain</span>
                <span className="font-medium">{formatNumber(minBudget)}</span>
              </div>
            </div>

            {/* Budget input */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                New URL Budget
              </label>
              <div className="flex gap-4 items-center">
                <input
                  type="range"
                  min={Math.max(minBudget, domain?.urlsUsed || 0)}
                  max={maxAvailableForThisDomain}
                  step={1000}
                  value={budgetValue}
                  onChange={(e) => setBudgetValue(parseInt(e.target.value))}
                  className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
                />
                <input
                  type="number"
                  min={Math.max(minBudget, domain?.urlsUsed || 0)}
                  max={maxAvailableForThisDomain}
                  step={1000}
                  value={budgetValue}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || minBudget;
                    setBudgetValue(Math.min(Math.max(val, Math.max(minBudget, domain?.urlsUsed || 0)), maxAvailableForThisDomain));
                  }}
                  className="w-32 px-3 py-2 border rounded-lg text-right font-mono focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                />
              </div>
              {(domain?.urlsUsed || 0) > 0 && (
                <p className="text-xs text-slate-500 mt-2">
                  Cannot reduce below {formatNumber(domain?.urlsUsed || 0)} (currently in use)
                </p>
              )}
            </div>

            {budgetError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {budgetError}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-3">
              <button
                onClick={handleCancelBudgetEdit}
                disabled={isSavingBudget}
                className="flex-1 py-2 border border-slate-300 rounded-lg font-medium hover:bg-slate-50 flex items-center justify-center gap-2"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                onClick={handleSaveBudget}
                disabled={isSavingBudget || budgetValue === currentDomainBudget}
                className="flex-1 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isSavingBudget ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Budget usage display */}
            <div className="flex items-end justify-between mb-2">
              <div>
                <span className="text-3xl font-bold">{formatNumber(domain?.urlsUsed || 0)}</span>
                <span className="text-slate-400 text-lg"> / {formatNumber(domain?.urlBudget || 0)}</span>
              </div>
              <span className="text-sm text-slate-500">
                {domain?.urlBudget ? Math.round(((domain?.urlsUsed || 0) / domain.urlBudget) * 100) : 0}% used
              </span>
            </div>
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full transition-all',
                  domain?.urlBudget && (domain?.urlsUsed || 0) / domain.urlBudget > 0.9
                    ? 'bg-red-500'
                    : domain?.urlBudget && (domain?.urlsUsed || 0) / domain.urlBudget > 0.7
                      ? 'bg-amber-500'
                      : 'bg-primary'
                )}
                style={{
                  width: `${domain?.urlBudget ? Math.min(((domain?.urlsUsed || 0) / domain.urlBudget) * 100, 100) : 0}%`,
                }}
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              {formatNumber((domain?.urlBudget || 0) - (domain?.urlsUsed || 0))} URLs remaining in budget
            </p>
          </div>
        )}
      </div>

      {/* IndexNow Settings - Only show for verified domains */}
      {domain.verified && (
        <div className="mb-6">
          <IndexNowSettings
            domainId={domain.id}
            domainName={domain.domain}
            isVerified={domain.verified}
            onStatusChange={() => refetch()}
          />
        </div>
      )}

      {/* llms.txt co-hosting - Only show for verified domains */}
      {domain.verified && (
        <div className="mb-6">
          <LlmsTxtSettings domainId={domain.id} isVerified={domain.verified} />
        </div>
      )}

      {/* Apex-proxy snippet generator. Pairs with the well-known files we
          serve at the subdomain — the snippet mounts them at the customer's
          apex (where agents look). See strategy.md §4 Priority 0. */}
      {domain.verified && (
        <div className="mb-6">
          <ApexProxySettings
            domainId={domain.id}
            subdomain={domain.domain}
            isVerified={domain.verified}
          />
        </div>
      )}

      {/* Agent surfaces: MCP Server Card, A2A Agent Card, agents.json,
          ai-agent.json, ai-plugin.json, oauth-protected-resource, agents.txt.
          See strategy.md §4 Priority 2. */}
      {domain.verified && (
        <div className="mb-6">
          <AgentSurfacesSettings domainId={domain.id} isVerified={domain.verified} />
        </div>
      )}

      {/* Bot access control: per-AI-bot allow/disallow + Content Signals
          rendered into /robots.txt. See strategy.md §4 Priority 3. */}
      {domain.verified && (
        <div className="mb-6">
          <BotAccessSettings domainId={domain.id} isVerified={domain.verified} />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sitemaps */}
        <div className="bg-white rounded-xl border shadow-sm">
          <div className="p-4 border-b">
            <h2 className="font-semibold">Sitemaps</h2>
          </div>
          {sitemaps.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              <p>No sitemaps generated yet.</p>
              <p className="text-sm">Upload URLs to generate your first sitemap.</p>
            </div>
          ) : (
            <div className="divide-y">
              {sitemaps.map((sitemap) => (
                <div key={sitemap.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <a
                        href={`https://${domain.domain}/${sitemap.filename}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        {sitemap.filename}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                      <p className="text-sm text-slate-500">
                        {formatNumber(sitemap.urlCount)} URLs | {formatBytes(sitemap.fileSize)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {!!sitemap.isIndex && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          Sitemap Index
                        </span>
                      )}
                      {sitemap.gscStatus === 'success' && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                          GSC ✓
                        </span>
                      )}
                      {sitemap.gscStatus === 'pending' && (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                          GSC Pending
                        </span>
                      )}
                      {sitemap.gscStatus === 'error' && (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                          GSC Error
                        </span>
                      )}
                      {sitemap.indexNowStatus === 'success' && sitemap.lastIndexNow && (
                        <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                          <Zap className="w-3 h-3" />
                          IndexNow
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-3 text-xs text-slate-400 mt-1">
                    {sitemap.lastGooglePing && (
                      <span>Pinged: {formatDate(sitemap.lastGooglePing)}</span>
                    )}
                    {sitemap.gscSubmittedAt && (
                      <span>GSC: {formatDate(sitemap.gscSubmittedAt)}</span>
                    )}
                    {sitemap.lastIndexNow && (
                      <span>IndexNow: {formatDate(sitemap.lastIndexNow)}</span>
                    )}
                  </div>
                  {sitemap.gscStatus === 'error' && sitemap.gscError && (
                    <GscErrorMessage
                      error={sitemap.gscError}
                      domain={domain.domain}
                      domainId={domain.id}
                      onRetrySuccess={() => refetch()}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* File Manager - always visible, with prepare mode when not verified */}
        <FileManager
          domain={domain.domain}
          domainId={domain.id}
          onSuccess={() => refetch()}
          prepareMode={!domain.verified}
        />
      </div>

      {/* DNS Verification prompt if not verified - shown below the grid */}
      {!domain.verified && (
        <div className="mt-6 bg-amber-50 rounded-xl border border-amber-200 p-6">
          <h3 className="font-semibold text-amber-800 mb-2">Complete DNS Verification</h3>
          <p className="text-sm text-amber-700 mb-4">
            Add this CNAME record to your DNS provider to enable sitemap hosting:
          </p>
          <div className="bg-white rounded-lg p-4 font-mono text-sm">
            <p><span className="text-slate-500">Type:</span> CNAME</p>
            <p><span className="text-slate-500">Name:</span> {domain.domain.split('.')[0]}</p>
            <p><span className="text-slate-500">Target:</span> {domain.targetCname}</p>
          </div>

          {verifyResult && (
            <div className={cn(
              'mt-4 p-3 rounded-lg text-sm flex items-start gap-2',
              verifyResult.success
                ? 'bg-green-100 border border-green-200 text-green-800'
                : 'bg-red-100 border border-red-200 text-red-800'
            )}>
              {verifyResult.success ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              )}
              <span>{verifyResult.message}</span>
            </div>
          )}

          <button
            onClick={handleVerify}
            disabled={isVerifying}
            className="mt-4 w-full py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isVerifying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify DNS'
            )}
          </button>
        </div>
      )}

      {/* Danger Zone */}
      <div className="mt-8">
        <DangerZone
          title="Delete this domain"
          description="Once you delete this domain, all associated sitemaps and data will be permanently removed."
          confirmationText={domain.domain}
          confirmationLabel="Type the domain name to confirm"
          buttonText="Delete Domain"
          warnings={[
            'Delete all sitemaps associated with this domain',
            'Remove the domain from your account',
            'Break any existing sitemap URLs pointing to this domain',
            'This action cannot be undone',
          ]}
          onDelete={async () => {
            await domainApi.delete(domain.id);
            queryClient.invalidateQueries({ queryKey: ['domains'] });
            await refreshAuth(); // Refresh usage stats
            navigate('/');
          }}
        />
      </div>
    </div>
  );
}

// Helper component to display user-friendly GSC error messages
function GscErrorMessage({ error, domain, domainId, onRetrySuccess }: {
  error: string;
  domain: string;
  domainId: string;
  onRetrySuccess: () => void;
}) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [retrySuccess, setRetrySuccess] = useState(false);

  // Parse the error to determine the type and show actionable guidance
  let message = 'GSC submission failed';
  let action = '';

  if (error.includes('sufficient permission') || error.includes('403')) {
    message = 'No permission to submit sitemaps for this domain';
    action = `Add "${domain}" (or its parent domain) to your Google Search Console and verify ownership.`;
  } else if (error.includes('not found') || error.includes('404')) {
    message = 'Domain not found in Google Search Console';
    action = `Add "${domain}" as a property in Google Search Console first.`;
  } else if (error.includes('No matching GSC property')) {
    message = 'No matching GSC property found';
    action = `Add "${domain}" (or its parent domain) as a property in Google Search Console.`;
  } else if (error.includes('Auto-submit failed')) {
    message = 'Auto-submit to GSC failed';
    action = 'Try regenerating your sitemap or check your GSC connection in Settings.';
  }

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await gscApi.submit(domainId);
      setRetrySuccess(true);
      // Wait a moment to show success, then refresh
      setTimeout(() => {
        onRetrySuccess();
      }, 1500);
    } catch (err) {
      // Keep showing error state
      setIsRetrying(false);
    }
  };

  // Show success state
  if (retrySuccess) {
    return (
      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
        <p className="text-green-700 font-medium flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4" />
          Successfully submitted to GSC!
        </p>
        <p className="text-green-600 text-xs mt-1">Refreshing...</p>
      </div>
    );
  }

  return (
    <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
      <p className="text-red-700 font-medium">{message}</p>
      <p className="text-red-600 mt-1">{action}</p>
      <div className="flex items-center gap-3 mt-2">
        <a
          href="https://search.google.com/search-console"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-red-700 hover:text-red-800 underline text-xs"
        >
          Open Google Search Console
          <ExternalLink className="w-3 h-3" />
        </a>
        <button
          onClick={handleRetry}
          disabled={isRetrying}
          title="Retry submitting existing sitemaps to GSC"
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium disabled:opacity-50"
          style={{ backgroundColor: '#ffd0a2', color: '#92400e' }}
        >
          {isRetrying ? (
            <>
              <Loader2 className="w-3 h-3 animate-spin" />
              Retrying...
            </>
          ) : (
            <>
              <RefreshCw className="w-3 h-3" />
              Retry GSC
            </>
          )}
        </button>
      </div>
    </div>
  );
}
